import React, { useState, useEffect, useMemo, useRef } from 'react';
import Swal from 'sweetalert2';
import { FaSearch, FaPlus, FaEdit, FaTrash, FaTimes, FaChevronDown, FaChevronUp, FaUserGraduate, FaUpload, FaImage, FaFileExport } from 'react-icons/fa';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import axiosInstance from '../../../api/axiosInstance';
import { Helmet } from "react-helmet-async";
import Pagination from '../../Pagination/Pagination';
import './AddScholar.css';
import statesDistrictsData from '../../../JsonData/india-states-districts-latest.json';

const SmartFilterDropdown = ({ value, placeholder, options, onSelect, onClear, searchPlaceholder, disabled }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const ref = useRef(null);
  const listRef = useRef(null);

  const filtered = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));

  useEffect(() => { setHighlighted(0); }, [search, open]);
  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open && listRef.current) {
      const el = listRef.current.querySelector('.admin-add-scholar-drop-highlighted');
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlighted, open]);

  const handleKey = e => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted(h => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered.length > 0 && highlighted < filtered.length) { onSelect(filtered[highlighted]); setOpen(false); setSearch(''); }
    } else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div className="admin-add-scholar-filter-wrap" ref={ref} style={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
      <div className={`admin-add-scholar-filter-trigger ${open ? 'active' : ''}`} onClick={() => { if(!disabled) { setOpen(o => !o); setSearch(''); } }}>
        <span className="admin-add-scholar-filter-text" style={{ color: value ? '#2c3e50' : '#334155', fontWeight: value ? '600' : '400' }}>
          {value || placeholder}
        </span>
        <div className="admin-add-scholar-trigger-icons">
          {value && !disabled && <FaTimes className="admin-add-scholar-clear-icon" onClick={(e) => { e.stopPropagation(); onClear(); }} title="Clear Filter" />}
          <FaChevronDown size={10} style={{ color: '#64748b' }} />
        </div>
      </div>
      {open && (
        <div className="admin-add-scholar-filter-menu">
          <input type="text" placeholder={searchPlaceholder || "Search..."} value={search} onChange={e => setSearch(e.target.value)} onKeyDown={handleKey} autoFocus />
          <ul ref={listRef}>
            {filtered.length > 0 ? filtered.map((o, i) => (
              <li key={i} className={i === highlighted ? 'admin-add-scholar-drop-highlighted' : ''} onMouseEnter={() => setHighlighted(i)} onClick={() => { onSelect(o); setOpen(false); setSearch(''); }}>
                {o}
              </li>
            )) : <div className="admin-add-scholar-drop-empty">No results found</div>}
          </ul>
        </div>
      )}
    </div>
  );
};

const initialFormState = { name: '', fatherName: '', contactNumber: '', state: '', district: '', address: '', image: null };
const PAGE_OPTIONS = [10, 20, 30, 40, 50, 100];

const AddScholar = () => {
  const [scholars, setScholars] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState(initialFormState);
  const [editId, setEditId] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => { fetchScholars(); }, []);

  const fetchScholars = async () => {
    try {
      const res = await axiosInstance.get('/api/scholars');
      setScholars(Array.isArray(res.data) ? res.data : (res.data?.data || []));
    } catch (error) { console.error(error); }
  };

  const allStates = useMemo(() => statesDistrictsData.map(item => item.state).filter(Boolean).sort(), []);
  const availableDistricts = useMemo(() => {
    if (form.state) {
      const found = statesDistrictsData.find(item => item.state === form.state);
      return found ? found.districts.sort() : [];
    }
    return [];
  }, [form.state]);

  // Extract unique years from when scholars/beneficiaries were added
  const availableYears = useMemo(() => {
    const years = scholars
      .map(s => s.createdAt ? new Date(s.createdAt).getFullYear().toString() : null)
      .filter(Boolean);
    return [...new Set(years)].sort((a, b) => b - a);
  }, [scholars]);

  const activeScholars = useMemo(() => {
    let list = scholars.filter(s => !s.isDeleted);
    
    // Filter by registration year
    if (yearFilter) {
      list = list.filter(s => s.createdAt && new Date(s.createdAt).getFullYear().toString() === yearFilter);
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(s => (s.name?.toLowerCase().includes(q)) || (s.contactNumber?.includes(q)) || (s.fatherName?.toLowerCase().includes(q)));
    }
    return list;
  }, [scholars, searchQuery, yearFilter]);

  useEffect(() => { setCurrentPage(1); }, [searchQuery, yearFilter]);

  const paginatedScholars = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return activeScholars.slice(start, start + itemsPerPage);
  }, [activeScholars, currentPage, itemsPerPage]);

  const handleExportExcel = async () => {
    if (activeScholars.length === 0) return Swal.fire('Notice', 'No data to export.', 'info');

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Beneficiary Profiles');

    // Title Block
    worksheet.mergeCells('A1:H1');
    worksheet.getCell('A1').value = 'RAJPUT SHIKSHA KOSH';
    worksheet.getCell('A1').font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FFB91C1C' } };
    worksheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' };

    // Subtitle Block
    worksheet.mergeCells('A2:H2');
    worksheet.getCell('A2').value = 'Beneficiary Profiles Report';
    worksheet.getCell('A2').font = { name: 'Arial', size: 12, bold: true, color: { argb: 'FF2C3E50' } };
    worksheet.getCell('A2').alignment = { vertical: 'middle', horizontal: 'center' };

    // Filter Information Row
    worksheet.mergeCells('A3:H3');
    const appliedYearText = yearFilter ? yearFilter : 'All Years';
    const appliedSearchText = searchQuery.trim() ? `"${searchQuery.trim()}"` : 'None';
    worksheet.getCell('A3').value = `Applied Filters — Year: ${appliedYearText} | Search: ${appliedSearchText}`;
    worksheet.getCell('A3').font = { name: 'Arial', size: 10, italic: true, color: { argb: 'FF555555' } };
    worksheet.getCell('A3').alignment = { vertical: 'middle', horizontal: 'center' };

    // Space before data table
    worksheet.addRow([]);

    // Table Header Row
    const headerRow = worksheet.addRow(['S.No', 'Student Name', 'Father Name', 'Contact Number', 'State', 'District', 'Address', 'Status']);
    headerRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF34495E' } };
      cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });

    // Populate Data Rows
    activeScholars.forEach((s, i) => {
      const row = worksheet.addRow([ i + 1, s.name, s.fatherName, s.contactNumber, s.state, s.district, s.address, s.isActive ? 'Active' : 'Disabled' ]);
      row.eachCell((cell) => {
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
      });
      row.getCell(1).alignment = { horizontal: 'center' };
      const statusCell = row.getCell(8);
      statusCell.alignment = { horizontal: 'center' };
      statusCell.font = { bold: true, color: { argb: s.isActive ? 'FF16A34A' : 'FFDC2626' } };
    });

    worksheet.columns = [ { width: 8 }, { width: 25 }, { width: 25 }, { width: 18 }, { width: 18 }, { width: 18 }, { width: 45 }, { width: 15 } ];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'Beneficiary_Profiles.xlsx');
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) { setForm({ ...form, image: file }); setImagePreview(URL.createObjectURL(file)); }
  };

  const handleSaveScholar = async () => {
    if (!form.name || !form.fatherName || !form.contactNumber || !form.state || !form.district || !form.address) {
      return Swal.fire('Warning', 'All fields except image are mandatory.', 'warning');
    }
    if (form.contactNumber.length !== 10) return Swal.fire('Warning', 'Contact Number must be exactly 10 digits.', 'warning');

    try {
      const formData = new FormData();
      Object.keys(form).forEach(key => { if (form[key] !== null) formData.append(key, form[key]); });

      if (editId) {
        await axiosInstance.put(`/api/scholars/${editId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' }});
        Swal.fire('Updated', 'Beneficiary profile updated successfully', 'success');
      } else {
        await axiosInstance.post('/api/scholars', formData, { headers: { 'Content-Type': 'multipart/form-data' }});
        Swal.fire('Added', 'New beneficiary added successfully', 'success');
      }
      resetForm(); fetchScholars();
    } catch (error) { Swal.fire('Error', error.response?.data?.message || 'Operation failed', 'error'); }
  };

  const handleEdit = (s) => { 
    setForm({
      name: s.name || '', fatherName: s.fatherName || '', contactNumber: s.contactNumber || '',
      state: s.state || '', district: s.district || '', address: s.address || '', image: null
    });
    setImagePreview(s.image || null); setEditId(s._id); setShowAddForm(true); window.scrollTo({ top: 0, behavior: 'smooth' }); 
  };
  
  const handleDelete = async (s) => {
    if (s.scholarships && s.scholarships.length > 0) return Swal.fire('Cannot Delete', 'This beneficiary has existing records. Please disable the profile instead.', 'error');
    const res = await Swal.fire({ title: 'Delete Beneficiary?', text: 'Mark beneficiary as deleted.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#e74c3c' });
    if (res.isConfirmed) { await axiosInstance.delete(`/api/scholars/${s._id}`); fetchScholars(); }
  };

  const toggleStatus = async (id, status) => { await axiosInstance.put(`/api/scholars/${id}/status`, { isActive: !status }); fetchScholars(); };
  const resetForm = () => { setShowAddForm(false); setForm(initialFormState); setEditId(null); setImagePreview(null); };

  return (
    <>
    <Helmet>
                <title>Add Scholar | Rajput Shiksha Kosh</title>
        
                <meta
                  name="description"
                  content="Manage scholar profiles for the Rajput Shiksha Kosh organization."
                />
              </Helmet>
    <div className="admin-add-scholar-container">
      <h2 className="admin-add-scholar-title"><FaUserGraduate style={{marginRight: '8px'}}/> Beneficiary Profile Management</h2>

      <div className="admin-add-scholar-controls">
        <div className="admin-add-scholar-search-wrapper">
          <FaSearch className="admin-add-scholar-search-icon" />
          <input type="text" className="admin-add-scholar-search-input" placeholder="Search Name, Father, Contact..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </div>
        
        <div className="admin-add-scholar-action-group">
          <select 
            className="admin-add-scholar-limit-select" 
            value={yearFilter} 
            onChange={(e) => setYearFilter(e.target.value)}
          >
            <option value="">Filter by Year (All)</option>
            {availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>

          <select className="admin-add-scholar-limit-select" value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
            {PAGE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt} / page</option>)}
          </select>
          <button className="admin-add-scholar-btn" onClick={handleExportExcel} title="Export to Excel">
            <FaFileExport /> Export
          </button>
          <button className="admin-add-scholar-btn admin-add-scholar-btn-add" onClick={() => showAddForm ? resetForm() : setShowAddForm(true)}>
            {showAddForm ? <><FaTimes /> Close Form</> : <><FaPlus /> Add Beneficiary</>}
          </button>
        </div>
      </div>

      {showAddForm && (
        <div className="admin-add-scholar-inline-form">
          <h3 className="admin-add-scholar-form-title">{editId ? '✏️ Edit Beneficiary Profile' : 'Add New Beneficiary Profile'}</h3>
          <div className="admin-add-scholar-form-main-grid">
            <div className="admin-add-scholar-form-fields">
              <div className="admin-add-scholar-form-grid">
                <div className="admin-add-scholar-form-group">
                  <label>Student Name <span className="admin-add-scholar-req">*</span></label>
                  <input type="text" value={form.name} onChange={e=>setForm({...form, name: e.target.value})} placeholder="Student Name" />
                </div>
                <div className="admin-add-scholar-form-group">
                  <label>Father Name <span className="admin-add-scholar-req">*</span></label>
                  <input type="text" value={form.fatherName} onChange={e=>setForm({...form, fatherName: e.target.value})} placeholder="Father Name" />
                </div>
                <div className="admin-add-scholar-form-group">
                  <label>Contact Number <span className="admin-add-scholar-req">*</span></label>
                  <input type="text" value={form.contactNumber} onChange={e=>setForm({...form, contactNumber: e.target.value.replace(/\D/g,'')})} placeholder="10-digit number" maxLength="10"/>
                </div>
                <div className="admin-add-scholar-form-group">
                  <label>State <span className="admin-add-scholar-req">*</span></label>
                  <SmartFilterDropdown value={form.state} placeholder="Select State" options={allStates} onSelect={(val) => setForm({...form, state: val, district: ''})} onClear={() => setForm({...form, state: '', district: ''})}/>
                </div>
                <div className="admin-add-scholar-form-group">
                  <label>District <span className="admin-add-scholar-req">*</span></label>
                  <SmartFilterDropdown value={form.district} placeholder="Select District" options={availableDistricts} onSelect={(val) => setForm({...form, district: val})} onClear={() => setForm({...form, district: ''})} disabled={!form.state}/>
                </div>
                <div className="admin-add-scholar-form-group admin-add-scholar-col-span-full">
                  <label>Complete Address <span className="admin-add-scholar-req">*</span></label>
                  <input type="text" value={form.address} onChange={e=>setForm({...form, address: e.target.value})} placeholder="Complete Address" />
                </div>
              </div>
            </div>
            <div className="admin-add-scholar-form-image-section">
              <label className="admin-add-scholar-img-label">Student Photo</label>
              <div className="admin-add-scholar-img-upload-box">
                {imagePreview ? <img src={imagePreview} alt="Preview" className="admin-add-scholar-img-preview" /> : <div className="admin-add-scholar-img-placeholder"><FaImage size={32} color="#94a3b8" /></div>}
                <input type="file" id="admin-scholar-img" accept="image/*" onChange={handleImageChange} hidden />
              </div>
              <label htmlFor="admin-scholar-img" className="admin-add-scholar-btn-upload"><FaUpload /> Choose Photo</label>
            </div>
          </div>
          <div className="admin-add-scholar-form-buttons">
            <button className="admin-add-scholar-btn-cancel" onClick={resetForm}>Cancel</button>
            <button className="admin-add-scholar-btn-confirm" onClick={handleSaveScholar}>{editId ? 'Update Beneficiary' : 'Save Beneficiary'}</button>
          </div>
        </div>
      )}

      <div className="admin-add-scholar-table-wrapper">
        <table className="admin-add-scholar-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th className="admin-add-scholar-col-img">Image</th>
              <th className="admin-add-scholar-align-left">Student Info</th>
              <th className="admin-add-scholar-col-desk">Contact Number</th>
              <th className="admin-add-scholar-col-desk">State</th>
              <th className="admin-add-scholar-col-desk">District</th>
              <th className="admin-add-scholar-col-status">Status</th>
              <th className="admin-add-scholar-col-action">Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedScholars.length === 0 ? <tr><td colSpan="8" style={{padding:'40px', textAlign:'center', color: '#64748b'}}>No records found.</td></tr> :
            paginatedScholars.map((s, i) => {
              const globalIndex = (currentPage - 1) * itemsPerPage + i;
              return (
              <React.Fragment key={s._id}>
                <tr className="admin-add-scholar-main-row">
                  <td>{globalIndex + 1}</td>
                  <td className="admin-add-scholar-col-img">
                    <div className="admin-add-scholar-td-img-wrap">{s.image ? <img src={s.image} alt={s.name} /> : <FaUserGraduate color="#94a3b8" size={24}/>}</div>
                  </td>
                  <td className="admin-add-scholar-align-left">
                    <div className="admin-add-scholar-name-wrapper" onClick={() => setExpandedRow(expandedRow === s._id ? null : s._id)}>
                      <div><span style={{fontWeight: 600}}>{s.name}</span><br/><span style={{fontSize: '0.8rem', color: '#64748b'}}>S/O {s.fatherName}</span></div>
                      <button className="admin-add-scholar-expand-btn">{expandedRow === s._id ? <FaChevronUp /> : <FaChevronDown />}</button>
                    </div>
                  </td>
                  <td className="admin-add-scholar-col-desk">{s.contactNumber}</td>
                  <td className="admin-add-scholar-col-desk">{s.state}</td>
                  <td className="admin-add-scholar-col-desk">{s.district}</td>
                  <td className="admin-add-scholar-col-status">
                    <label className="admin-add-scholar-switch"><input type="checkbox" checked={s.isActive !== false} onChange={() => toggleStatus(s._id, s.isActive !== false)} /><span className="admin-add-scholar-slider" /></label>
                    <div style={{ fontSize: '0.75rem', marginTop: '4px', color: s.isActive !== false ? '#e67e22' : '#94a3b8', fontWeight: '600' }}>{s.isActive !== false ? 'Active' : 'Disabled'}</div>
                  </td>
                  <td className="admin-add-scholar-col-action">
                    <div className="admin-add-scholar-action-btns">
                      <button className="admin-add-scholar-btn-edit" onClick={() => handleEdit(s)}><FaEdit /></button>
                      <button className="admin-add-scholar-btn-delete" onClick={() => handleDelete(s)}><FaTrash /></button>
                    </div>
                  </td>
                </tr>
                {expandedRow === s._id && (
                  <tr className="admin-add-scholar-expanded-row">
                    <td colSpan="8">
                      <div className="admin-add-scholar-expanded-content">
                        <div className="admin-add-scholar-exp-item">
                          <span className="admin-add-scholar-exp-label">Created At</span>
                          <span className="admin-add-scholar-exp-value">
                            {s.createdAt ? new Date(s.createdAt).toLocaleDateString('en-IN', { dateStyle: 'medium' }) : 'N/A'}
                          </span>
                        </div>
                        <div className="admin-add-scholar-exp-item admin-add-scholar-exp-mob">
                          <span className="admin-add-scholar-exp-label">Contact Number</span>
                          <span className="admin-add-scholar-exp-value">{s.contactNumber}</span>
                        </div>
                        <div className="admin-add-scholar-exp-item admin-add-scholar-exp-desk">
                          <span className="admin-add-scholar-exp-label">State</span>
                          <span className="admin-add-scholar-exp-value">{s.state}</span>
                        </div>
                        <div className="admin-add-scholar-exp-item admin-add-scholar-exp-desk">
                          <span className="admin-add-scholar-exp-label">District</span>
                          <span className="admin-add-scholar-exp-value">{s.district}</span>
                        </div>
                        <div className="admin-add-scholar-exp-item admin-add-scholar-col-span-full">
                          <span className="admin-add-scholar-exp-label">Address</span>
                          <span className="admin-add-scholar-exp-value">{s.address}</span>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            )})}
          </tbody>
        </table>
      </div>

      {activeScholars.length > 0 && (
        <Pagination totalItems={activeScholars.length} itemsPerPage={itemsPerPage} currentPage={currentPage} onPageChange={setCurrentPage} />
      )}
    </div>
    </>
  );
};

export default AddScholar;